import { memo, type MouseEvent, useCallback, useEffect, useRef, useState } from "react";

import type { QueueJob, QueueJobStatus } from "@/lib/api";
import { formatDateTime, formatDuration } from "@/lib/format";

const STATUS_CLS: Record<QueueJobStatus, string> = {
  queued: "border-amber-800 bg-amber-900/40 text-amber-300",
  dispatched: "border-sky-800 bg-sky-900/40 text-sky-300",
  preempting: "border-fuchsia-800 bg-fuchsia-900/40 text-fuchsia-300",
  preempted: "border-violet-800 bg-violet-900/40 text-violet-300",
  started: "border-blue-800 bg-blue-900/40 text-blue-300",
  progress: "border-blue-700 bg-blue-900/50 text-blue-200",
  retry: "border-orange-800 bg-orange-900/40 text-orange-300",
  success: "border-emerald-800 bg-emerald-900/40 text-emerald-300",
  failure: "border-red-800 bg-red-900/40 text-red-300",
  canceled: "border-ink-600 bg-ink-800 text-ink-400",
};

const STATUS_LABEL: Record<QueueJobStatus, string> = {
  queued: "Na fila",
  dispatched: "Despachado",
  preempting: "Pausando",
  preempted: "Retomado",
  started: "Iniciado",
  progress: "Em progresso",
  retry: "Retry",
  success: "Sucesso",
  failure: "Falha",
  canceled: "Cancelado",
};

const TYPE_LABEL: Record<string, string> = {
  "viewer.render": "Render v1",
  "viewer.preprepare": "Preprepare",
  "viewer.rehydrate": "Recuperação",
  "viewer.layers": "Camadas",
  "render.preview": "Render",
  barcode: "Barcode",
};

const PRIORITIES = [
  { value: 0, label: "Background 0" },
  { value: 30, label: "Normal 30" },
  { value: 70, label: "Alta 70" },
  { value: 100, label: "Urgente 100" },
];

//: A mesma grade do cabeçalho e das linhas — as duas têm de casar coluna a
//: coluna, então a constante é uma só.
const COL_TEMPLATE = "6rem 6.5rem 8rem 3rem 4.5rem minmax(10rem,4fr) 11rem 5rem";


function priorityLabel(priority: number): string {
  if (priority >= 100) return "Urgente";
  if (priority >= 70) return "Alta";
  if (priority <= 0) return "Background";
  return "Normal";
}

function jobDuration(job: QueueJob): string {
  if (!job.started_at) return "—";
  const endTs = job.finished_at ? new Date(job.finished_at).getTime() : Date.now();
  const secs = (endTs - new Date(job.started_at).getTime()) / 1000;
  if (secs < 0) return "—";
  return formatDuration(secs);
}

interface Props {
  jobs: QueueJob[];
  total: number;
  offset: number;
  limit: number;
  selectedJobId: string | null;
  loading: boolean;
  isAdmin: boolean;
  onSelectJob: (id: string) => void;
  onUpdatePriority: (id: string, priority: number) => Promise<void>;
  onNextPage: () => void;
  onPrevPage: () => void;
}

interface QueueRowProps {
  job: QueueJob;
  isSelected: boolean;
  onSelectJob: (id: string) => void;
  onContextMenu: (event: MouseEvent<HTMLElement>, job: QueueJob) => void;
}

/**
 * Uma linha da fila.
 *
 * `memo` com igualdade por identidade do job: o poll de 5 s troca o array
 * inteiro, e sem isto as 50 linhas re-renderizavam a cada volta mesmo quando
 * nenhum job tinha mudado. O `queueStore` faz a outra metade do trato —
 * preserva o objeto anterior quando o job veio igual —, então "não mudou"
 * aqui significa literalmente o mesmo objeto.
 */
const QueueRow = memo(function QueueRow({
  job,
  isSelected,
  onSelectJob,
  onContextMenu,
}: QueueRowProps) {
  return (
    <button
      type="button"
      onClick={() => onSelectJob(job.id)}
      onContextMenu={(event) => onContextMenu(event, job)}
      className={`grid w-full cursor-pointer items-center gap-x-3 border-b border-ink-800 px-3 py-2.5 text-left text-xs transition-colors last:border-b-0 hover:bg-ink-800 ${
        isSelected ? "bg-ink-800" : ""
      }`}
      style={{ gridTemplateColumns: COL_TEMPLATE }}
    >
      <span className="truncate text-ink-300">{TYPE_LABEL[job.type] ?? job.type}</span>
      <span className="truncate text-ink-300" title={String(job.priority)}>
        {priorityLabel(job.priority)}
      </span>
      <span>
        <span
          className={`rounded border px-1.5 py-0.5 text-xs ${STATUS_CLS[job.status]}`}
          title={
            job.status === "preempted"
              ? "Interrompido por prioridade; o trabalho seguiu num novo job de retomada"
              : job.status === "preempting"
                ? "Pausa solicitada; aguardando checkpoint seguro"
                : undefined
          }
        >
          {STATUS_LABEL[job.status] ?? job.status}
        </span>
      </span>
      <span className="text-right tabular-nums text-ink-400">
        {job.status === "progress" ||
        job.status === "started" ||
        job.status === "preempting"
          ? `${job.progress}%`
          : "—"}
      </span>
      <span className="tabular-nums text-ink-300" title={job.operational_note ?? undefined}>
        {Math.max(1, job.attempts)}ª
      </span>
      <span
        className="truncate text-ink-300"
        title={job.file_path ?? job.file_name ?? job.file_id ?? ""}
      >
        {job.file_name ?? (job.file_id ? job.file_id.slice(0, 8) + "…" : "—")}
        {job.operational_note && job.status !== "preempted" && (
          <span className="ml-2 text-[11px] text-sky-300">Retomada</span>
        )}
      </span>
      <span className="text-ink-400">{formatDateTime(job.queued_at)}</span>
      <span className="text-right tabular-nums text-ink-400">{jobDuration(job)}</span>
    </button>
  );
});


export function QueueTable({
  jobs,
  total,
  offset,
  limit,
  selectedJobId,
  loading,
  isAdmin,
  onSelectJob,
  onUpdatePriority,
  onNextPage,
  onPrevPage,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [contextMenu, setContextMenu] = useState<{
    job: QueueJob;
    x: number;
    y: number;
  } | null>(null);
  const [priorityLoading, setPriorityLoading] = useState(false);
  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + limit, total);

  // Tipo | Prioridade | Status | % | Tentativa | Arquivo (elástico) | Data | Duração
  const canUpdatePriority =
    contextMenu !== null &&
    isAdmin &&
    contextMenu.job.type.startsWith("viewer.") &&
    contextMenu.job.status === "queued";

  useEffect(() => {
    if (contextMenu === null) return;

    function close() {
      setContextMenu(null);
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") close();
    }

    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [contextMenu]);

  // Estável entre renders: é prop de uma linha memoizada, e uma função nova a
  // cada render desfaria o memo de todas as 50.
  const openContextMenu = useCallback((event: MouseEvent<HTMLElement>, job: QueueJob) => {
    event.preventDefault();
    const menuWidth = 220;
    const menuHeight = 172;
    setContextMenu({
      job,
      x: Math.min(event.clientX, window.innerWidth - menuWidth - 8),
      y: Math.min(event.clientY, window.innerHeight - menuHeight - 8),
    });
  }, []);

  async function handlePriority(priority: number) {
    if (!contextMenu || !canUpdatePriority) return;
    setPriorityLoading(true);
    try {
      await onUpdatePriority(contextMenu.job.id, priority);
      setContextMenu(null);
    } finally {
      setPriorityLoading(false);
    }
  }

  return (
    <div ref={containerRef} className="relative flex flex-1 flex-col overflow-hidden rounded-lg border border-ink-700">
      {/* Header */}
      <div
        className="grid select-none gap-x-3 border-b border-ink-700 bg-ink-850 px-3 py-2 text-xs font-medium uppercase tracking-wide text-ink-400"
        style={{ gridTemplateColumns: COL_TEMPLATE }}
      >
        <span>Tipo</span>
        <span>Prioridade</span>
        <span>Status</span>
        <span className="text-right">%</span>
        <span title="Número da tentativa deste job (retry automático ou manual)">Tentativa</span>
        <span>Nome do Arquivo</span>
        <span>Enfileirado</span>
        <span className="text-right">Duração</span>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {loading && jobs.length === 0 && (
          <div className="flex items-center justify-center py-12 text-sm text-ink-500">
            Carregando…
          </div>
        )}
        {!loading && jobs.length === 0 && (
          <div className="flex items-center justify-center py-12 text-sm text-ink-500">
            Nenhum job encontrado.
          </div>
        )}
        {jobs.map((job) => (
          <QueueRow
            key={job.id}
            job={job}
            isSelected={job.id === selectedJobId}
            onSelectJob={onSelectJob}
            onContextMenu={openContextMenu}
          />
        ))}
      </div>

      {contextMenu && (
        <div
          className="fixed z-50 w-56 overflow-hidden rounded border border-ink-700 bg-ink-900 py-1 text-xs shadow-xl shadow-black/40"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="border-b border-ink-800 px-3 py-2">
            <div className="font-medium text-ink-100">Alterar prioridade</div>
            <div className="mt-0.5 truncate text-[11px] text-ink-500">
              {contextMenu.job.file_name ?? contextMenu.job.id}
            </div>
          </div>
          {!canUpdatePriority && (
            <div className="px-3 py-2 text-ink-500">
              Disponível apenas para jobs Viewer em fila.
            </div>
          )}
          {canUpdatePriority &&
            PRIORITIES.map((priority) => (
              <button
                key={priority.value}
                type="button"
                disabled={priorityLoading || contextMenu.job.priority === priority.value}
                onClick={() => void handlePriority(priority.value)}
                className="flex w-full items-center justify-between px-3 py-2 text-left text-ink-300 hover:bg-ink-800 hover:text-ink-100 disabled:cursor-not-allowed disabled:text-ink-600"
              >
                <span>{priority.label}</span>
                {contextMenu.job.priority === priority.value && <span>Atual</span>}
              </button>
            ))}
        </div>
      )}

      {/* Footer / pagination */}
      <div className="flex items-center justify-between border-t border-ink-700 bg-ink-850 px-3 py-2 text-xs text-ink-400">
        <span>
          {total > 0 ? `${from}–${to} de ${total}` : "0 resultados"}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={offset === 0}
            onClick={onPrevPage}
            className="rounded border border-ink-700 bg-ink-800 px-3 py-1 text-xs text-ink-300 disabled:cursor-not-allowed disabled:opacity-40 hover:not-disabled:border-ink-500 hover:not-disabled:text-ink-100"
          >
            ← Anterior
          </button>
          <span>
            {currentPage} / {totalPages}
          </span>
          <button
            type="button"
            disabled={offset + limit >= total}
            onClick={onNextPage}
            className="rounded border border-ink-700 bg-ink-800 px-3 py-1 text-xs text-ink-300 disabled:cursor-not-allowed disabled:opacity-40 hover:not-disabled:border-ink-500 hover:not-disabled:text-ink-100"
          >
            Próximo →
          </button>
        </div>
      </div>
    </div>
  );
}
